import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { captureAttribution } from "@/lib/api";
import {
  applyAttributionTouch,
  buildAttributionTouch,
  clearAttributionStorage,
  type AttributionTouch,
} from "@/lib/attribution";
import { useCookieConsent } from "@/hooks/useCookieConsent";

export default function AttributionCaptureController() {
  const location = useLocation();
  const { preferences } = useCookieConsent();
  const lastPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!preferences) return;
    const currentPath = `${location.pathname}${location.search}`;
    const referrer =
      lastPathRef.current === null
        ? document.referrer
        : `${window.location.origin}${lastPathRef.current}`;

    if (!preferences.analytics && !preferences.advertising) {
      clearAttributionStorage();
      return;
    }
    lastPathRef.current = currentPath;

    const touch = buildAttributionTouch({
      pathname: location.pathname,
      search: location.search,
      referrer,
      consent: preferences,
    });
    if (!touch) return;

    const state = applyAttributionTouch(touch);
    const serverTouch: AttributionTouch | null = state?.last_touch ?? state?.first_touch ?? touch;
    if (!serverTouch) return;
    if (import.meta.env.MODE === "test") return;

    void captureAttribution(serverTouch, preferences.advertising ? "advertising" : "analytics").catch(() => {
      // Attribution must not block navigation or checkout.
    });
  }, [location.pathname, location.search, preferences]);

  return null;
}
